import { useState, useRef, useEffect } from 'react';
import type { SelectOption } from './MobileMultipleSelect';
import * as S from './styles';
import { ChevronDownIcon } from '../../../assets/icons/ChevronDownIcon';
import ReactDOM from 'react-dom';
import CheckIcon from '../../../assets/icons/CheckIcon';
import { useShadowContainer } from '@skin-seoul/design-system';

interface DesktopMultipleSelectProps {
  options: SelectOption[];
  values: string[];
  onChange: (value: string[]) => void;
  title?: string;
  width?: string | number;
}

export const DesktopMultipleSelect = ({
  options,
  values: value,
  onChange,
  title = 'Select an option',
  width,
}: DesktopMultipleSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const optionListRef = useRef<HTMLDivElement>(null);
  const shadowContainer = useShadowContainer();

  const handleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    setTimeout(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + 4,
          left: rect.left,
          width: buttonRef.current.offsetWidth,
          zIndex: 2000,
        });
      }
    }, 0);
  };

  const handleSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleClickOutside = (event: MouseEvent) => {
      if (
        optionListRef.current &&
        !optionListRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const portalContainer = shadowContainer ?? document.body;

  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        ...(width ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
      }}
    >
      <S.SelectButton ref={buttonRef} onClick={handleOpen} type="button">
        {title}
        <span>
          <ChevronDownIcon isOpen={isOpen} />
        </span>
      </S.SelectButton>
      {isOpen &&
        ReactDOM.createPortal(
          <S.MultipleOptionList
            ref={optionListRef}
            style={{
              ...dropdownStyle,
              backgroundColor: '#ffffff',
              border: '1px solid #dcdcdc',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              padding: '8px 12px',
              overflowY: 'auto',
              maxHeight: '300px',
              pointerEvents: 'auto',
            }}
          >
            {options.map((option, idx) => (
              <S.MultipleOptionItem
                key={option.value}
                isSelected={value.includes(option.value)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(option.value);
                }}
                style={{
                  padding: '15px 0px',
                  borderBottom: idx < options.length - 1 ? '1px solid #dcdcdc' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  color: '#737373',
                  fontSize: '14px',
                  fontWeight: '400',
                }}
              >
                <S.MultipleOptionLabel>{option.label}</S.MultipleOptionLabel>
                <S.MultipleOptionCheckbox
                  checked={value.includes(option.value)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    border: value.includes(option.value) ? 'none' : '2px solid #70737C',
                    borderRadius: '8px',
                    background: value.includes(option.value) ? '#3D1690' : '#fff',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  {value.includes(option.value) && <CheckIcon />}
                </S.MultipleOptionCheckbox>
              </S.MultipleOptionItem>
            ))}
          </S.MultipleOptionList>,
          portalContainer,
        )}
    </div>
  );
};
